import React, { useState } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    Container,
    Row,
    Col,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    Input,
    Label,
    Tooltip,
} from "reactstrap";
import { useGetProductByIdQuery } from "slices/InventoryManagement/inventoryManagementSlice";
import classnames from "classnames";

// Import Swiper styles
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/thumbs";

import { FreeMode, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

interface ProductDetailsModalProps {
    isOpen: boolean;
    toggle: () => void;
    productId: number;
}

const ProductDetailsModal: React.FC<ProductDetailsModalProps> = ({
    isOpen,
    toggle,
    productId,
}) => {
    const [customActiveTab, setCustomActiveTab] = useState<string>("1");
    const { data: product, isLoading, error } = useGetProductByIdQuery(productId);

    const toggleCustom = (tab: string) => {
        if (customActiveTab !== tab) {
            setCustomActiveTab(tab);
        }
    };

    if (isLoading) {
        return (
            <Modal isOpen={isOpen} toggle={toggle} size="xl" centered>
                <ModalBody>
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </ModalBody>
            </Modal>
        );
    }
    if (error) return <div>Error loading product details</div>;
    if (!product) return null;

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl" centered>
            <ModalHeader toggle={toggle}>
                Product Details
                {/* {product.name} */}
            </ModalHeader>
            <ModalBody>
                <Container fluid>
                    <Row className="gx-lg-5">
                        <Col xl={4} md={8} className="mx-auto">
                            <div className="product-img-slider">
                                <Swiper
                                    modules={[Navigation, FreeMode]}
                                    navigation={true}
                                    className="swiper product-thumbnail-slider p-2 rounded bg-light"
                                >
                                    {product.attachment_urls && product.attachment_urls.length > 0 ? (
                                        product.attachment_urls.map((attachment: any, index: number) => (
                                            <SwiperSlide key={index}>
                                                <img
                                                    src={attachment.file_url}
                                                    alt={attachment.name}
                                                    className="img-fluid d-block"
                                                    style={{ maxHeight: "300px", objectFit: "contain" }}
                                                />
                                            </SwiperSlide>
                                        ))
                                    ) : product.thumbnail_url ? (
                                        <SwiperSlide>
                                            <img
                                                src={product.thumbnail_url}
                                                alt={product.name}
                                                className="img-fluid d-block"
                                                style={{ maxHeight: "300px", objectFit: "contain" }}
                                            />
                                        </SwiperSlide>
                                    ) : (
                                        <SwiperSlide>
                                            <div
                                                className="bg-light rounded d-flex align-items-center justify-content-center"
                                                style={{ height: "300px" }}
                                            >
                                                <i className="ri-image-line fs-1 text-muted"></i>
                                            </div>
                                        </SwiperSlide>
                                    )}
                                </Swiper>
                            </div>
                        </Col>

                        <Col xl={8}>
                            <div className="mt-xl-0 mt-5">
                                <div className="d-flex">
                                    <div className="flex-grow-1">
                                        <h4>{product.name}</h4>
                                        <div className="hstack gap-3 flex-wrap">
                                            <div>
                                                <span className="text-primary d-block">
                                                    {product.brand_company}
                                                </span>
                                            </div>
                                            <div className="vr"></div>
                                            <div className="text-muted">
                                                Category:{" "}
                                                <span className="text-body fw-medium">
                                                    {product.category_name}
                                                </span>
                                            </div>
                                            <div className="vr"></div>
                                            <div className="text-muted">
                                                Model:{" "}
                                                <span className="text-body fw-medium">
                                                    {product.model_number}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <Row className="mt-4">
                                    <Col lg={3} sm={6}>
                                        <div className="p-2 border border-dashed rounded">
                                            <div className="d-flex align-items-center">
                                                <div className="avatar-sm me-2">
                                                    <div className="avatar-title rounded bg-transparent text-success fs-24">
                                                        <i className="ri-price-tag-3-line"></i>
                                                    </div>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <p className="text-muted mb-1">Category:</p>
                                                    <h5 className="mb-0">{product.category_name}</h5>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>

                                    <Col lg={3} sm={6}>
                                        <div className="p-2 border border-dashed rounded">
                                            <div className="d-flex align-items-center">
                                                <div className="avatar-sm me-2">
                                                    <div className="avatar-title rounded bg-transparent text-success fs-24">
                                                        <i className="ri-ruler-line"></i>
                                                    </div>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <p className="text-muted mb-1">Measurement:</p>
                                                    <h5 className="mb-0">{product.measurement_name}</h5>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>

                                    <Col lg={3} sm={6}>
                                        <div className="p-2 border border-dashed rounded">
                                            <div className="d-flex align-items-center">
                                                <div className="avatar-sm me-2">
                                                    <div className="avatar-title rounded bg-transparent text-success fs-24">
                                                        <i className="ri-calendar-event-line"></i>
                                                    </div>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <p className="text-muted mb-1">Created:</p>
                                                    <h5 className="mb-0">
                                                        {new Date(product.created_at).toLocaleDateString()}
                                                    </h5>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>

                                

                                <div className="mt-4 text-muted">
                                    <h5 className="fs-15">Description:</h5>
                                    <p>
                                        {product.description || "No description available."}
                                    </p>
                                </div>

                                {product.tag_names && product.tag_names.length > 0 && (
                                    <div className="mt-3">
                                        <h5 className="fs-15">Tags:</h5>
                                        <div className="d-flex flex-wrap gap-2">
                                            {product.tag_names.map((tag: any, index: number) => (
                                                <span
                                                    key={index}
                                                    className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25"
                                                >
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* <div className="product-content mt-5">
                  <h5 className="fs-15 mb-3">Product Details:</h5>
                  <Nav tabs className="nav-tabs-custom nav-success">
                    <NavItem>
                      <NavLink
                        style={{ cursor: "pointer" }}
                        className={classnames({
                          active: customActiveTab === "1",
                        })}
                        onClick={() => {
                          toggleCustom("1");
                        }}
                      >
                        Specification
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        style={{ cursor: "pointer" }}
                        className={classnames({
                          active: customActiveTab === "2",
                        })}
                        onClick={() => {
                          toggleCustom("2");
                        }}
                      >
                        Additional Details
                      </NavLink>
                    </NavItem>
                  </Nav>

                  <TabContent
                    activeTab={customActiveTab}
                    className="border border-top-0 p-4"
                    id="nav-tabContent"
                  >
                    <TabPane tabId="1">
                      <div className="table-responsive">
                        <table className="table mb-0">
                          <tbody>
                            <tr>
                              <th scope="row" style={{ width: "200px" }}>
                                Category
                              </th>
                              <td>{product.category_name}</td>
                            </tr>
                            <tr>
                              <th scope="row">Brand</th>
                              <td>{product.brand_company}</td>
                            </tr>
                            <tr>
                              <th scope="row">Model Number</th>
                              <td>{product.model_number}</td>
                            </tr>
                            <tr>
                              <th scope="row">Measurement</th>
                              <td>{product.measurement_name}</td>
                            </tr>
                            {product.size && (
                              <tr>
                                <th scope="row">Size</th>
                                <td>{product.size}</td>
                              </tr>
                            )}
                            {product.weight && (
                              <tr>
                                <th scope="row">Weight</th>
                                <td>{product.weight}</td>
                              </tr>
                            )}
                            {product.dimensions && (
                              <tr>
                                <th scope="row">Dimensions</th>
                                <td>{product.dimensions}</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </TabPane>
                    <TabPane tabId="2">
                      <div>
                        <h5 className="mb-3">Additional Information</h5>
                        {product.attributes && Object.keys(product.attributes).length > 0 ? (
                          <div className="table-responsive">
                            <table className="table mb-0">
                              <tbody>
                                {Object.entries(product.attributes).map(([key, value]) => (
                                  <tr key={key}>
                                    <th scope="row" style={{ width: "200px" }}>
                                      {key}
                                    </th>
                                    <td>{String(value)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p>No additional information available.</p>
                        )}
                      </div>
                    </TabPane>
                  </TabContent>
                </div> */}
                            </div>
                        </Col>
                    </Row>
                </Container>
            </ModalBody>
        </Modal>
    );
};

export default ProductDetailsModal;